import React from 'react';
import classNames from 'classnames';
import ButtonGroup from '@ssg/common/Components/ButtonGroup';
import { faCheck, faScanner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';

interface Props {
	setScannedId: React.Dispatch<React.SetStateAction<string | undefined>>;
	multiple?: boolean;
	allowedPrefixes?: string[];
}

const NOT_FOUND = 'SCANNED_TEXT_NOT_FOUND';

const BarcodeScanner: React.FC<Props> = ({ setScannedId, multiple = false, allowedPrefixes }): React.ReactElement => {
	const { t } = useTranslation();

	const useMovableHandscanner = useFlag(FeatureFlagEnums.USE_MOVABLE_HANDSCANNER);

	//const [active, setActive] = React.useState(false);
	// const [data, setData] = React.useState(NOT_FOUND);
	const [success, setSuccess] = React.useState(false);
	const [html5QrcodeScanner, setHtml5QrcodeScanner] = React.useState<Html5QrcodeScanner>();
	const [scanMode, setScanMode] = React.useState<'CAMERA' | 'SCANNER'>(useMovableHandscanner ? 'SCANNER' : 'CAMERA');

	React.useEffect(() => {
		return () => {
			html5QrcodeScanner?.clear();
		};
	}, [html5QrcodeScanner]);

	// React.useEffect(() => {
	// 	if (data !== NOT_FOUND && (typeof allowedPrefixes === 'undefined' || allowedPrefixes.some(prefix => data.startsWith(prefix)))) {
	// 		if (!multiple) {
	// 			setScannedId(data);
	// 		} else {
	// 			setScannedId(current => {
	// 				if (typeof current === 'undefined' || current.length === 0) {
	// 					return data;
	// 				}

	// 				if (current.includes(data)) {
	// 					return current;
	// 				}

	// 				return `${current},${data}`;
	// 			});

	// 			setSuccess(true);
	// 			window.setTimeout(() => setSuccess(false), 1500);
	// 		}
	// 	}
	// }, [data, setScannedId, multiple, allowedPrefixes]);

	const successHandler = React.useCallback(
		(scannedString: string) => {
			// setData(scannedString);
			if (scannedString !== NOT_FOUND && (typeof allowedPrefixes === 'undefined' || allowedPrefixes.some(prefix => scannedString.startsWith(prefix)))) {
				if (!multiple) {
					setScannedId(scannedString);
				} else {
					setScannedId(current => {
						if (typeof current === 'undefined' || current.length === 0) {
							return scannedString;
						}

						if (current.includes(scannedString)) {
							return current;
						}

						return `${current},${scannedString}`;
					});

					setSuccess(true);
					window.setTimeout(() => setSuccess(false), 1500);
				}
			}
		},
		[allowedPrefixes, multiple, setScannedId],
	);

	// const successHandler = React.useCallback(async (scannedString: string) => {
	// 	setData(scannedString);
	// }, []);

	React.useEffect(() => {
		if (scanMode !== 'CAMERA') return;
		if (typeof html5QrcodeScanner === 'undefined') {
			const newHtml5QrcodeScanner = new Html5QrcodeScanner(
				'reader',
				{
					fps: 10,
					qrbox: { width: 250, height: 250 },
					supportedScanTypes: [],
					showTorchButtonIfSupported: true,
				},
				/* verbose= */ false,
			);
			setHtml5QrcodeScanner(newHtml5QrcodeScanner);
		}
		if (typeof html5QrcodeScanner !== 'undefined') {
			html5QrcodeScanner.render(
				(data: string) => successHandler(data),
				(err: string) => console.log('err ->', err),
			);
		}
	}, [html5QrcodeScanner, scanMode, successHandler]);

	const [keyboardBuffer, setKeyboardBuffer] = React.useState('');

	React.useEffect(() => {
		if (scanMode !== 'SCANNER') return;
		const handleKeyDown = (e: KeyboardEvent) => {
			// Ignore if focus is on an input/textarea
			const tag = (e.target as HTMLElement).tagName;
			if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;

			if (e.key === 'Enter') {
				if (keyboardBuffer.length > 0) {
					const sanitizedBuffer = keyboardBuffer.replace(/'/g, '|');
					console.log({
						keyboardBuffer: keyboardBuffer,
						sanitizedBuffer: sanitizedBuffer,
					});
					successHandler(sanitizedBuffer);
					setKeyboardBuffer('');
				}
			} else if (e.key.length === 1) {
				setKeyboardBuffer(prev => prev + e.key);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [keyboardBuffer, scanMode, successHandler]);

	return (
		<>
			<div className="relative mx-auto" style={{ width: 500, height: 600 }}>
				{scanMode === 'CAMERA' && (
					<div className="absolute z-40">
						<div className={classNames({ hidden: success })}>
							<div style={{ width: '500px' }} id="reader"></div>
						</div>
					</div>
				)}

				{scanMode === 'SCANNER' && !success && (
					<div className="flex flex-col items-center justify-center">
						<FontAwesomeIcon icon={faScanner} className="ml-2 animate-pulse" size="5x" />
					</div>
				)}

				{success && (
					<div className="absolute z-50 mx-auto w-full text-center text-2xl font-semibold" style={{ top: '50%', transform: 'translateY(-50%)' }}>
						<div className="text-green-calm">
							{t('common.scanned')}
							<FontAwesomeIcon icon={faCheck} className="ml-2" size="lg" />
						</div>
					</div>
				)}
			</div>
			{useMovableHandscanner && !success && (
				<div className="text-center mt-4">
					<ButtonGroup
						items={[
							{
								title: 'Håndscanner',
								isActive: scanMode === 'SCANNER',
								onClick: (): void => setScanMode('SCANNER'),
							},
							{
								title: 'Kamera',
								isActive: scanMode === 'CAMERA',
								onClick: (): void => setScanMode('CAMERA'),
							},
						]}
					/>
				</div>
			)}
		</>
	);
};

export default BarcodeScanner;

